import React, { useEffect, useMemo, useRef, useState } from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { notificationsAPI } from '../services/api';
import {
  enableNotificationDevice,
  getCurrentPushSubscription,
  isIosDevice,
  isNotificationSupported,
  isStandalone,
} from '../utils/notificationSubscription';
import {
  recordPromptAction,
  wasPromptedThisLogin,
} from '../utils/notificationDiscovery';

export default function NotificationDiscoveryPrompt({ user, loginGeneration }) {
  const mobile = useMediaQuery('(max-width:599.95px)');
  const userId = user?._id || user?.id || '';
  const eligibleRole = user?.role === 'coach' || user?.role === 'parent';
  const supported = useMemo(() => isNotificationSupported(), []);
  const iosNeedsInstall = useMemo(
    () => supported && isIosDevice() && !isStandalone(),
    [supported]
  );
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [publicKey, setPublicKey] = useState('');
  const [error, setError] = useState('');
  const statusLiveRef = useRef(null);
  const errorLiveRef = useRef(null);
  const closeTimerRef = useRef(null);

  const announceStatus = (message) => {
    if (statusLiveRef.current) statusLiveRef.current.textContent = message;
  };

  const announceError = (message) => {
    if (errorLiveRef.current) errorLiveRef.current.textContent = message;
  };

  useEffect(() => {
    let cancelled = false;
    if (
      !eligibleRole
      || !userId
      || !loginGeneration
      || !supported
      || iosNeedsInstall
      || window.Notification.permission === 'denied'
      || wasPromptedThisLogin(userId, loginGeneration)
    ) {
      return undefined;
    }

    getCurrentPushSubscription()
      .then((subscription) => {
        if (cancelled || subscription) return null;
        return notificationsAPI.getConfig();
      })
      .then((configResponse) => {
        if (cancelled || !configResponse) return;
        const key = configResponse.data?.data?.publicKey || '';
        if (!key) return;
        recordPromptAction(userId, loginGeneration, 'shown');
        setPublicKey(key);
        setOpen(true);
      })
      .catch((probeError) => {
        console.warn('Notification discovery probe failed:', probeError?.message);
      });

    return () => {
      cancelled = true;
    };
  }, [eligibleRole, iosNeedsInstall, loginGeneration, supported, userId]);

  useEffect(() => () => {
    if (closeTimerRef.current) window.clearTimeout(closeTimerRef.current);
  }, []);

  const dismiss = (action = 'not_now') => {
    recordPromptAction(userId, loginGeneration, action);
    setOpen(false);
  };

  const enable = async () => {
    setBusy(true);
    setError('');
    announceError('');
    announceStatus('Enabling notifications, please wait.');
    try {
      const result = await enableNotificationDevice(publicKey);
      if (result.permission === 'granted') {
        announceStatus('Notifications enabled on this device.');
        recordPromptAction(userId, loginGeneration, 'enabled');
        closeTimerRef.current = window.setTimeout(() => setOpen(false), 600);
        return;
      }
      const message = result.permission === 'denied'
        ? 'Notifications are blocked. Open Notification settings for instructions to allow them.'
        : 'Notification permission was not granted. Select Not now or try again.';
      recordPromptAction(
        userId,
        loginGeneration,
        result.permission === 'denied' ? 'blocked' : 'not_now'
      );
      setError(message);
      announceStatus('');
      announceError(message);
    } catch (enableError) {
      const message =
        enableError.response?.data?.error
        || 'Notifications could not be enabled. Open Settings, then Notifications, to try again.';
      setError(message);
      announceStatus('');
      announceError(message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => dismiss('not_now')}
      fullWidth
      maxWidth="sm"
      fullScreen={mobile}
      aria-labelledby="notification-discovery-title"
      aria-describedby="notification-discovery-description"
    >
      <DialogTitle id="notification-discovery-title">
        Get notified when your team posts a new message.
      </DialogTitle>
      <DialogContent>
        <span
          ref={statusLiveRef}
          role="status"
          aria-live="polite"
          aria-atomic="true"
          style={{
            position: 'absolute',
            width: 1,
            height: 1,
            margin: -1,
            overflow: 'hidden',
            clip: 'rect(0 0 0 0)',
            whiteSpace: 'nowrap',
          }}
        />
        <span
          ref={errorLiveRef}
          role="alert"
          aria-live="assertive"
          aria-atomic="true"
          style={{
            position: 'absolute',
            width: 1,
            height: 1,
            margin: -1,
            overflow: 'hidden',
            clip: 'rect(0 0 0 0)',
            whiteSpace: 'nowrap',
          }}
        />
        <Typography id="notification-discovery-description" sx={{ color: 'var(--text-secondary)' }}>
          EliteEdge can send a private alert without showing message or player details on your lock screen.
        </Typography>
        {error && (
          <Typography sx={{ color: 'error.main', mt: 2 }}>
            {error}
          </Typography>
        )}
      </DialogContent>
      <DialogActions
        sx={{
          p: 2,
          pb: mobile ? 'calc(16px + env(safe-area-inset-bottom, 0px))' : 2,
          gap: 1,
          flexWrap: 'wrap',
        }}
      >
        <Button
          type="button"
          variant="text"
          onClick={() => dismiss('not_now')}
          disabled={busy}
          sx={{ minHeight: 44, textTransform: 'none' }}
        >
          Not now
        </Button>
        <Button
          type="button"
          variant="contained"
          onClick={enable}
          disabled={busy}
          sx={{
            minHeight: 44,
            textTransform: 'none',
          }}
        >
          {busy ? 'Enabling...' : 'Enable notifications'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
